import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AudienceApiService, AudienceSociodemoRow } from '../../../../services/audience-api.service';

interface AgeBar {
  label: string;
  count: number;
  percent: number;
}

type Gender = 'total' | 'male' | 'female';

/**
 * Sociodemo tab of the advanced "Additional Info" card. Rebuilt from admin-dashboard
 * `audience-insights-sociodemo` in the new plain-HTML idiom (no chart.js): a Male/Female split plus a
 * per-age-range breakdown that switches with the selected gender.
 */
@Component({
  selector: 'app-audience-sociodemo-tab',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './audience-sociodemo-tab.component.html',
  styleUrls: ['./audience-sociodemo-tab.component.scss']
})
export class AudienceSociodemoTabComponent implements OnChanges {
  @Input() audienceId: string | null = null;

  loading = false;
  error = false;
  noData = false;

  malePercent = 0;
  femalePercent = 0;
  selectedGender: Gender = 'total';

  private maleData: AgeBar[] = [];
  private femaleData: AgeBar[] = [];
  private totalData: AgeBar[] = [];

  private loadedFor: string | null = null;

  constructor(private audienceApi: AudienceApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['audienceId'] && this.audienceId && this.audienceId !== this.loadedFor) {
      this.load(this.audienceId);
    }
  }

  private load(audienceId: string): void {
    this.loadedFor = audienceId;
    this.loading = true;
    this.error = false;
    this.noData = false;
    this.selectedGender = 'total';
    this.audienceApi.getAudienceSociodemo(audienceId).subscribe({
      next: (rows) => {
        if (this.loadedFor !== audienceId) {
          return;
        }
        this.build(rows);
        this.loading = false;
      },
      error: () => {
        if (this.loadedFor !== audienceId) {
          return;
        }
        this.error = true;
        this.loading = false;
      }
    });
  }

  private build(rows: AudienceSociodemoRow[]): void {
    const isAge = (r: AudienceSociodemoRow) => r.AgesRange !== 'Total' && r.AgesRange !== '' && r.AgesRange !== 'null';
    const totalMale = rows.find(r => r.Gender === 'M' && r.AgesRange === 'Total');
    const totalFemale = rows.find(r => r.Gender === 'F' && r.AgesRange === 'Total');

    if (!totalMale || !totalFemale || !rows.length) {
      this.noData = true;
      this.maleData = this.femaleData = this.totalData = [];
      return;
    }

    const mTot = this.num(totalMale.AudienceGenderVolumes);
    const fTot = this.num(totalFemale.AudienceGenderVolumes);
    const known = mTot + fTot;
    this.malePercent = known ? (mTot / known) * 100 : 0;
    this.femalePercent = known ? (fTot / known) * 100 : 0;

    this.maleData = this.toBars(rows.filter(r => r.Gender === 'M' && isAge(r)), mTot);
    this.femaleData = this.toBars(rows.filter(r => r.Gender === 'F' && isAge(r)), fTot);
    const generic = rows.filter(r => r.Gender === 'Generic' && isAge(r));
    const genericTotal = generic.reduce((s, r) => s + this.num(r.AudienceGenderVolumes), 0);
    this.totalData = this.toBars(generic, genericTotal);
  }

  private toBars(rows: AudienceSociodemoRow[], total: number): AgeBar[] {
    return rows.map(r => {
      const count = this.num(r.AudienceGenderVolumes);
      return { label: r.AgesRange, count, percent: total ? (count / total) * 100 : 0 };
    });
  }

  selectGender(gender: Gender): void {
    this.selectedGender = this.selectedGender === gender && gender !== 'total' ? 'total' : gender;
  }

  get ageBars(): AgeBar[] {
    if (this.selectedGender === 'male') return this.maleData;
    if (this.selectedGender === 'female') return this.femaleData;
    return this.totalData;
  }

  get hasData(): boolean {
    return !this.noData && (this.malePercent > 0 || this.femalePercent > 0);
  }

  num(v: number | string | null | undefined): number {
    return Number(v) || 0;
  }

  formatNumber(value: number): string {
    return value.toLocaleString('it-IT');
  }

  trackByLabel(_index: number, bar: AgeBar): string {
    return bar.label;
  }
}
